import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  DisablableOption 
} from "@models";
import { 
  Advantage,
  Character,
  CharacterFeatureSource,
  CharacterSheetInterface,
  MinimumRoll,
  Modifier,
  Proficiency,
  Skill,
  SKILLS,
} from "@dndModels";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import SelectSkillBox from "@dndComponents/select/SelectSkillBox";
import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import FloatMenu from "@components/input/menus/FloatMenu";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";
import CharacterSkillInput from "@dndComponents/character/skills/CharacterSkillInput";

interface Props {
  char: CharacterSheetInterface;
  disabled: boolean;
}

const prof_override_options: DisablableOption[] = [
  { id: "-1", text: "Don't Override", disabled: false, reason: "" },
  { id: "0", text: "Not Proficient", disabled: false, reason: "" },
  { id: "0.5", text: "Half Proficiency", disabled: false, reason: "" },
  { id: "1", text: "Proficient", disabled: false, reason: "" },
  { id: "2", text: "Expertise", disabled: false, reason: "" }
];

const customization_map: { [type: string]: string } = {
  "Override Proficiencies": "Proficiency Override",
  "Extra Modifiers": "Modifier",
  "Override Modifiers": "Override"
};

export default function CharacterSkillsButton({ 
  char,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [view, setView] = useState("Current");

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderCurrent = () => {
    const return_us: any[] = [];
    const INT = char.mind.current_ability_scores.INT;
    if (char instanceof Character && char.c_options.bonus_skill_profs_for_int && INT > 0) {
      return_us.push(
      );
      const ids = char.spirit.bonus_skill_profs_ids;
      for (let i = 0; i < INT; ++i) {
        return_us.push(
          <Grid
            key={i}
            size={{
              xs: 12
            }}>
            <SelectSkillBox
              name={"Skill" + (INT > 1 ? ` ${i + 1}` : "")}
              value={ids[i]}
              color={ids[i] === "" ? "blue" : ""}
              ignore_us={char.mind.proficiencies.filter(o => o.proficiency_id !== ids[i] && o.type === "Skill").map(o => {
                return o.proficiency_id;
              })}
              onChange={(changed: string) => {
                char.spirit.bonus_skill_profs_ids[i] = changed;
                char.subscription_holder.recalculate();
              }}
            />
          </Grid>
        );
      }
    }
    return (
      <>
        <Grid
          className="centered"
          style={{ 
              fontWeight: "bold", fontSize: "12px"
            }}
          size={{
            xs: 12
          }}>
          Current Skills
        </Grid>
        { SKILLS.sort((a,b) => { return a.name.localeCompare(b.name); }).map(skill => {
          return (
            <Grid
              key={skill._id}
              size={{
                xs: 12
              }}>
              <CharacterSkillInput
                char={char}
                skill={skill}
              />
            </Grid>
          );
        })}
      </>
    );
  }

  const renderSkillFeatures = () => {
    const skill_profs = char.mind.proficiency_features.filter(cf => 
      cf.floating_feature instanceof Proficiency &&
        cf.floating_feature.proficiency_types.includes("Skill"));
    const skill_modifiers = char.mind.modifier_features.filter(cf => 
      cf.floating_feature instanceof Modifier && 
        ["Ability Check","Skill Check"].includes(cf.floating_feature.modifies));
    const skill_miscs = char.mind.misc_features.filter(cf => 
      (cf.floating_feature instanceof Advantage &&
        cf.floating_feature.type === "Ability Checks") ||
      (cf.floating_feature instanceof MinimumRoll &&
        (cf.floating_feature.types.includes("ability") ||
          cf.floating_feature.types.includes("skill"))));
    const skill_features = [...skill_profs,...skill_modifiers,...skill_miscs];
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          Skill Features
        </Grid>
        { skill_features.sort((a,b) => { return a.name.localeCompare(b.name) }).map((char_feature, key) => {
          const source = char.char_feature_sources.find(o => 
            o.true_id === char_feature.source_true_id);
          if (source) {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                <CharacterFeatureFromSource
                  char={char}
                  obj={char_feature}
                  source={source}
                />
              </Grid>
            );
          }
          return null;
        })}
      </>
    );
  }

  const renderBonusSkillProfs = () => {
    const return_us: any[] = [];
    const INT = char.mind.current_ability_scores.INT;
    if (char instanceof Character && char.c_options.bonus_skill_profs_for_int && INT > 0) {
      return_us.push(
        <Grid
          key="header"
          className="centered"
          style={{ 
              fontWeight: "bold", fontSize: "12px"
            }}
          size={{
            xs: 12
          }}>
          Bonus Skill Proficiencies for INT modifier ({ INT })
        </Grid>
      );
      const ids = char.spirit.bonus_skill_profs_ids;
      for (let i = 0; i < INT; ++i) {
        return_us.push(
          <Grid
            key={i}
            size={{
              xs: 12
            }}>
            <SelectSkillBox
              name={"Skill" + (INT > 1 ? ` ${i + 1}` : "")}
              value={ids[i]}
              color={ids[i] === "" ? "blue" : ""}
              ignore_us={char.mind.proficiencies.filter(o => o.proficiency_id !== ids[i] && o.type === "Skill").map(o => {
                return o.proficiency_id;
              })}
              onChange={(changed: string) => {
                char.spirit.bonus_skill_profs_ids[i] = changed;
                char.subscription_holder.recalculate();
              }}
            />
          </Grid>
        );
      }
    }
    return return_us;
  }

  const renderSkillCustomizations = () => {
    return (
      <>
        <Grid
          style={{ fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          { view }
        </Grid>
        { SKILLS.sort((a,b) => { return a.name.localeCompare(b.name); }).map(skill => {
          return (
            <Grid
              key={skill._id}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              { renderSkillCustomization(skill, customization_map[view]) }
            </Grid>
          );
        })}
      </>
    );
  }

  const renderSkillCustomization = (skill: Skill, customization_type: string) => {
    // const parent = char.skill_customizations[skill._id];
    // const customization = parent ? parent[customization_type] : null;
    // return (
    //   <>
    //     <Grid
    //       size={{
    //         xs: 6
    //       }}>
    //       { !reloading && 
    //         <CharacterSkillInput
    //           character={character}
    //           char={char}
    //           skill={skill}
    //         />
    //       }
    //     </Grid>
    //     <Grid
    //       size={{
    //         xs: customization && customization.value !== null ? 4 : 6
    //       }}>
    //       { customization_type === "Proficiency Override" ? 
    //         <SelectStringBox
    //           name=""
    //           options={prof_override_options}
    //           value={ customization && customization.value !== null ? `${customization.value}` : "-1" }
    //           onChange={(changed: string) => {
    //             if (!char.skill_customizations[skill._id]) {
    //               char.skill_customizations[skill._id] = { [customization_type]: { value: null, notes: "" }};
    //             } else if (!char.skill_customizations[skill._id][customization_type]) {
    //               char.skill_customizations[skill._id][customization_type] = { value: null, notes: "" };
    //             }
    //             if (changed === "-1") {
    //               delete char.skill_customizations[skill._id][customization_type];
    //             } else {
    //               char.skill_customizations[skill._id][customization_type].value = +changed;
    //             }
    //             character.subscription_holder.recalculate();
    //             setReloading(true);
    //           }}
    //         />
    //       :
    //         <StringBox
    //           name="Value"
    //           value={ customization && customization.value !== null ? `${customization.value}` : "" }
    //           onBlur={(changed: string) => {
    //             if (!char.skill_customizations[skill._id]) {
    //               char.skill_customizations[skill._id] = { [customization_type]: { value: null, notes: "" }};
    //             } else if (!char.skill_customizations[skill._id][customization_type]) {
    //               char.skill_customizations[skill._id][customization_type] = { value: null, notes: "" };
    //             }
    //             if (changed === "") {
    //               delete char.skill_customizations[skill._id][customization_type];
    //             } else {
    //               char.skill_customizations[skill._id][customization_type].value = +changed;
    //             }
    //             character.subscription_holder.recalculate();
    //             setReloading(true);
    //           }}
    //         />
    //       }
    //     </Grid>
    //     { customization && customization.value !== null &&
    //       <Grid
    //         size={{
    //           xs: 2
    //         }}>
    //         <StringBox
    //           name={ `Source/Notes` }
    //           value={ customization ? customization.notes : "" }
    //           onBlur={(changed: string) => {
    //             if (!char.skill_customizations[skill._id]) {
    //               char.skill_customizations[skill._id] = { [customization_type]: { value: null, notes: "" }};
    //             } else if (!char.skill_customizations[skill._id][customization_type]) {
    //               char.skill_customizations[skill._id][customization_type] = { value: null, notes: "" };
    //             }
    //             char.skill_customizations[skill._id][customization_type].notes = changed;
    //             character.subscription_holder.recalculate();
    //           }}
    //         />
    //       </Grid>
    //     }
    //   </>
    // );
    return null;
  }

  const renderView = () => {
    switch(view) {
      case "Current": return renderCurrent();
      case "Features": return renderSkillFeatures();
      case "Bonus Proficiencies": return renderBonusSkillProfs();
      default: return renderSkillCustomizations();
    }
  }

  const renderSkillsInput = () => {
    const options = char instanceof Character && char.c_options.bonus_skill_profs_for_int && char.mind.current_ability_scores.INT > 0 ? 
      ["Current","Features","Bonus Proficiencies","Override Proficiencies","Extra Modifiers","Override Modifiers"] :
      ["Current","Features","Override Proficiencies","Extra Modifiers","Override Modifiers"];
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          className="centered header"
          size={{
            xs: 12
          }}>
          Manage Skills
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <FloatMenu
            options={options}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        { renderView() }
      </Grid>
    );
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        className={`centered header ${ disabled ? "" : "clickable"} ${ !disabled && char instanceof Character && char.c_options.bonus_skill_profs_for_int && char.spirit.bonus_skill_profs_ids.filter(o => o === "").length > 0 ? "Flashing" : ""}`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
          }
        }}
        size={{
          xs: 12
        }}>
        Skills
      </Grid>
      <ModalOrDrawer
        open={ open } 
        onClose={() => {
          setOpen(false);
          char.subscription_holder.modalClosed();
        }}>
        { open && renderSkillsInput() }
      </ModalOrDrawer>
    </Grid>
  );
}
